// What each engine module cost to download: the page's resource timings matched by a URL pattern per module (the
// game names its chunks, e.g. `/rapier/`), each file re-read from the cache and gzipped in the browser
// (CompressionStream) for a size that doesn't depend on how the server happened to compress it.

/** One module's files and sizes. */
export interface ModuleBytes {
  /** The matched resource URLs. */
  files: string[];
  /** Bytes as decoded (what the browser parsed; once measured, without inline source maps). */
  bytes: number;
  /** Bytes on the wire as served (0 from a memory cache or a server that doesn't report it). */
  transferBytes: number;
  /** Gzipped bytes, measured here; null until measured or when the browser can't gzip. */
  gzipBytes: number | null;
}

/** The parts of a resource timing entry read here. */
export interface ResourceEntryLike {
  name: string;
  decodedBodySize: number;
  transferSize: number;
}

/** The resource entries whose URL matches `pattern` (source maps excluded). */
export function matchResources(entries: readonly ResourceEntryLike[], pattern: RegExp): ResourceEntryLike[] {
  const seen = new Set<string>();
  return entries.filter((e) => {
    if (!pattern.test(e.name) || /\.map(\?|$)/.test(e.name) || seen.has(e.name)) return false;
    seen.add(e.name);
    return true;
  });
}

/** Remove inline (data URL) source maps from a script. */
export const stripInlineSourceMap = (code: string): string => code.replace(/\n?\/\/# sourceMappingURL=data:[^\n]*/g, "");

/** Gzip a body and count the bytes (CompressionStream; null where the browser has none). */
export async function gzipLength(body: BodyInit | ReadableStream<Uint8Array>): Promise<number | null> {
  if (typeof CompressionStream === "undefined") return null;
  const stream = (body instanceof ReadableStream ? body : new Response(body).body!).pipeThrough(new CompressionStream("gzip"));
  const reader = stream.getReader();
  let n = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    n += value.byteLength;
  }
  return n;
}

/**
 * Sizes per module from the page's resources. `patterns` maps a module name to its URL pattern. With `gzip`, each file
 * is fetched again (from the cache) and gzipped here; that costs a moment, so call it after boot.
 */
export async function moduleBytes(
  patterns: Readonly<Record<string, RegExp>>,
  o: { entries?: readonly ResourceEntryLike[]; gzip?: boolean; fetch?: (url: string) => Promise<Response> } = {},
): Promise<Record<string, ModuleBytes>> {
  const entries = o.entries ?? (performance.getEntriesByType("resource") as unknown as ResourceEntryLike[]);
  const get = o.fetch ?? ((u: string) => fetch(u));
  const out: Record<string, ModuleBytes> = {};
  for (const [name, re] of Object.entries(patterns)) {
    const hits = matchResources(entries, re);
    const m: ModuleBytes = { files: hits.map((h) => h.name), bytes: hits.reduce((s, h) => s + h.decodedBodySize, 0), transferBytes: hits.reduce((s, h) => s + h.transferSize, 0), gzipBytes: null };
    if (o.gzip && hits.length) {
      let total = 0;
      let raw = 0;
      for (const h of hits) {
        // a dev server inlines source maps (a base64 copy of the map); they never ship, so they don't count
        const code = new TextEncoder().encode(stripInlineSourceMap(await (await get(h.name)).text()));
        const n = await gzipLength(code);
        if (n === null) {
          total = NaN;
          break;
        }
        total += n;
        raw += code.byteLength;
      }
      m.gzipBytes = Number.isNaN(total) ? null : total;
      if (!Number.isNaN(total)) m.bytes = raw;
    }
    out[name] = m;
  }
  return out;
}
